import { useState, useEffect } from 'react';
import { verifyBiometric, isBiometricEnabled } from '../services/biometricService';
import toast from 'react-hot-toast';

export default function BiometricLock({ onUnlock }) {
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState(null);

  const handleVerify = async () => {
    setIsVerifying(true);
    setError(null);

    try {
      const verified = await verifyBiometric();
      if (verified) {
        toast.success('Biometric verified!');
        onUnlock();
      } else {
        setError('Verification failed');
        toast.error('Biometric verification failed');
      }
    } catch (err) {
      console.error('Biometric error:', err);
      setError(err.message || 'Verification failed');
      toast.error('Biometric verification failed');
    } finally {
      setIsVerifying(false);
    }
  };

  // Auto-trigger on mount
  useEffect(() => {
    console.log('🔐 BiometricLock mounted');
    if (isBiometricEnabled()) {
      console.log('🔐 Auto-triggering biometric verification...');
      handleVerify();
    }
  }, []);

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center z-50">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-full mx-4">
        <div className="text-center">
          {/* Lock Icon */}
          <div className="mx-auto w-24 h-24 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center mb-6">
            <svg
              className="w-12 h-12 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>

          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Galaxy Mini
          </h2>
          <p className="text-gray-600 mb-8">
            Unlock with Face ID or Fingerprint
          </p>

          {/* Fingerprint/Face ID Animation */}
          {isVerifying ? (
            <div className="mb-6">
              <div className="mx-auto w-20 h-20 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-gray-500 mt-4">Verifying...</p>
            </div>
          ) : (
            <button
              onClick={handleVerify}
              disabled={isVerifying}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center justify-center">
                <svg
                  className="w-6 h-6 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4"
                  />
                </svg>
                Unlock with Face ID / Fingerprint
              </div>
            </button>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Fallback option */}
          <button
            onClick={onUnlock}
            className="mt-4 text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
